import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Plus,
  RefreshCcw,
  Search,
  SearchX,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ContactTable,
  ContactTableSkeleton,
} from "@/components/contacts/ContactTable";
import { ContactDetailSheet } from "@/components/contacts/ContactDetailSheet";
import { ContactEditDialog } from "@/components/contacts/ContactEditDialog";
import { ContactsBulkActionsBar } from "@/components/contacts/ContactsBulkActionsBar";
import { CreateContactDialog } from "@/components/contacts/CreateContactDialog";
import { DeleteContactDialog } from "@/components/contacts/DeleteContactDialog";
import {
  exportContactsCSV,
  getContact,
  listContacts,
} from "@/api/contacts";
import { ContactFilterControls } from "@/components/contacts/ContactFilterControls";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { usePermissions } from "@/hooks/usePermissions";
import { useContactFilterState } from "@/hooks/useContactFilters";
import { useDelayedLoading } from "@/hooks/useDelayedLoading";
import { useColumnPreferences } from "@/hooks/useColumnPreferences";
import { ColumnSelector } from "@/components/table/ColumnSelector";
import { CONTACT_COLUMNS } from "@/lib/contactColumnCatalog";
import { formatNumber } from "@/lib/formatters";
import type { Contact, SavedViewRead } from "@/api/types";
// F6 split ViewsBar into two surfaces: a dropdown (ViewsDropdown)
// in row 2 and a Save / Discard cluster (ViewActionsCluster) sitting
// to its right when a view is dirty. ContactsPage keeps the
// ColumnSelector in the page header (row 1, top-right) — it didn't
// have the chips bar that CompaniesPage relocates the cluster to.
import { ViewsDropdown } from "@/components/views/ViewsDropdown";
import { ViewActionsCluster } from "@/components/views/ViewActionsCluster";
import { SaveAsNewViewModal } from "@/components/views/SaveAsNewViewModal";
import { filtersEqual, useViews } from "@/hooks/useViews";

const PAGE_SIZE = 50;


type LoadStatus = "loading" | "ok" | "error";

export default function ContactsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { project } = useCurrentProject();
  const perms = usePermissions();
  const canViewContacts = perms.canViewContacts;
  const canEdit = perms.canEditContacts;
  const canCreate = perms.canCreateContacts;
  const canDelete = perms.canDeleteContacts;
  const canExport = perms.canExportContacts;
  const canCreateViews = perms.canCreateViews;
  const canShareViews = perms.canShareViews;
  const slug = project?.slug ?? null;

  const contactFilters = useContactFilterState(slug, canViewContacts);
  const {
    search,
    setSearch,
    debouncedSearch,
    companyFilter,
    dmFilter,
    enrichmentFilter,
    companies,
    companyById,
    listParams,
    filtersJson: currentFiltersJson,
    applyView,
    anyFilterActive,
  } = contactFilters;
  const [page, setPage] = useState(1);

  const [items, setItems] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const showSkeleton = useDelayedLoading(status === "loading", 200);


  const [activeContact, setActiveContact] = useState<Contact | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [exporting, setExporting] = useState(false);

  // F6: parent owns the SaveAsNewViewModal open state so both the
  // ViewsDropdown's "+ Create new view" footer and the cluster's
  // chevron split-button can share the same controlled instance.
  const [saveAsNewOpen, setSaveAsNewOpen] = useState(false);

  const clearSelection = () => setSelectedIds(new Set());

  // Route-guard: kick the user out if they don't have view permission.
  useEffect(() => {
    if (slug && !canViewContacts) {
      toast.info("You don't have access to this page.");
      navigate(`/projects/${slug}/dashboard`, { replace: true });
    }
  }, [slug, canViewContacts, navigate]);

  // Deep-link: when ?contact=ID is present (e.g. from the global Cmd+K
  // palette), fetch that contact and pop the detail sheet.
  useEffect(() => {
    const contactParam = searchParams.get("contact");
    if (!slug || !contactParam || !canViewContacts) return;
    const contactId = Number(contactParam);
    if (!Number.isFinite(contactId)) return;
    let cancelled = false;
    void (async () => {
      try {
        const c = await getContact(slug, contactId);
        if (cancelled) return;
        setActiveContact(c);
        setSheetOpen(true);
      } catch {
        // Silently ignore — the contact may have been deleted or moved.
      } finally {
        // Strip the param so navigating back doesn't re-open the sheet.
        if (!cancelled) {
          const next = new URLSearchParams(searchParams);
          next.delete("contact");
          setSearchParams(next, { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, searchParams, canViewContacts, setSearchParams]);

  // Reset page on filter / search changes.
  useEffect(() => {
    setPage(1);
  }, [companyFilter, dmFilter, enrichmentFilter, debouncedSearch]);

  // Reset selection on filter / search / page changes — the visible row set
  // changes, so prior selection is no longer meaningful.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [companyFilter, dmFilter, enrichmentFilter, debouncedSearch, page]);

  const load = useCallback(async () => {
    if (!slug) return;
    setStatus("loading");
    setErrorMessage(null);
    try {
      const res = await listContacts(slug, {
        ...listParams,
        q: debouncedSearch.trim() || undefined,
        page,
        page_size: PAGE_SIZE,
      });
      setItems(res.items);
      setTotal(res.total);
      setStatus("ok");
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
      setStatus("error");
    }
  }, [slug, listParams, debouncedSearch, page]);

  useEffect(() => {
    if (canViewContacts) void load();
  }, [load, canViewContacts]);


  function handleRowClick(contact: Contact) {
    setActiveContact(contact);
    setSheetOpen(true);
  }

  function handleEdit() {
    setSheetOpen(false);
    setEditOpen(true);
  }

  function handleSaved(updated: Contact) {
    setItems((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    setActiveContact(updated);
  }

  function handleCreated() {
    void load();
  }

  function handleAskDelete() {
    setSheetOpen(false);
    setDeleteOpen(true);
  }

  function handleDeleted(id: number) {
    setItems((prev) => prev.filter((c) => c.id !== id));
    setTotal((t) => Math.max(0, t - 1));
    setActiveContact(null);
  }

  function handleBulkUpdated(
    ids: number[],
    update: { is_decision_maker?: boolean },
  ) {
    const idSet = new Set(ids);
    setItems((prev) =>
      prev.map((c) => {
        if (!idSet.has(c.id)) return c;
        return {
          ...c,
          is_decision_maker:
            update.is_decision_maker ?? c.is_decision_maker,
        };
      }),
    );
  }

  function handleBulkDeleted(ids: number[]) {
    const idSet = new Set(ids);
    setItems((prev) => prev.filter((c) => !idSet.has(c.id)));
    setTotal((t) => Math.max(0, t - ids.length));
    if (activeContact && idSet.has(activeContact.id)) {
      setActiveContact(null);
      setSheetOpen(false);
    }
  }

  async function handleExport() {
    if (!slug) return;
    setExporting(true);
    try {
      await exportContactsCSV(slug, { ...listParams, q: debouncedSearch.trim() || undefined });
      const noun = total === 1 ? "contact" : "contacts";
      toast.success(`CSV exported (${formatNumber(total)} ${noun})`);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 422) {
        toast.error("Too many rows. Apply filters to reduce.");
      } else {
        toast.error("Export failed. Try again.");
      }
    } finally {
      setExporting(false);
    }
  }

  // ─── Saved views ────────────────────────────────────────────────────────
  const viewsApi = useViews(slug, "contacts");
  const {
    views,
    isLoading: viewsLoading,
    loaded: viewsLoaded,
    defaultView,
    create: createView,
    rename: renameView,
    // setFilters from useViews dropped here — F3 removed
    // onUpdateFiltersFromCurrent, the only consumer.
    setShared: setViewShared,
    patch: patchView,
    remove: deleteView,
    toggleDefault,
  } = viewsApi;


  // Active view is tracked by intent, not derived from filter equality —
  // tweaking a filter no longer drops the selection. `isDirty` flags when
  // the current state has drifted from the saved view.
  const [activeViewId, setActiveViewId] = useState<number | null>(null);
  const activeView = useMemo<SavedViewRead | null>(
    () => views.find((v) => v.id === activeViewId) ?? null,
    [views, activeViewId],
  );
  // Column preferences — hardcoded catalog (Contacts shape doesn't
  // change between project_types). Hook owns its own localStorage at
  // prism.{slug}.contacts.columns; an active saved view's `columns`
  // takes priority when present.
  const {
    activeKeys: visibleColumnKeys,
    isDirty: columnsDirty,
    setActiveKeys: setVisibleColumnKeys,
    reset: resetColumns,
  } = useColumnPreferences({
    slug,
    scope: "contacts",
    available: CONTACT_COLUMNS,
    activeView,
  });

  const filtersDirty = useMemo<boolean>(
    () =>
      !!activeView && !filtersEqual(activeView.filters, currentFiltersJson),
    [activeView, currentFiltersJson],
  );
  const isDirty = filtersDirty || (!!activeView && columnsDirty);

  const applyViewFilters = applyView;

  // F3-bis: resetAllFilters was only used by the now-removed
  // handlePickAll. ContactsPage has no global "Clear filters"
  // button today; if one is added later, restore this helper.

  function handlePickView(view: SavedViewRead) {
    setActiveViewId(view.id);
    applyViewFilters(view);
  }

  function handleResetActiveView() {
    if (!activeView) return;
    applyViewFilters(activeView);
    // The hook re-resolves its baseline from the active view, so reset
    // pulls the view's saved columns back in (or defaults if the view
    // doesn't carry any).
    resetColumns();
  }

  async function handleUpdateActiveView() {
    if (!activeView) return;
    try {
      // PATCH filters AND columns in one round-trip — the saved view
      // captures the full state of the page.
      await patchView(activeView.id, {
        filters: currentFiltersJson,
        columns: visibleColumnKeys,
      });
      toast.success(`View "${activeView.name}" updated`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  const autoAppliedRef = useRef(false);
  useEffect(() => {
    if (!viewsLoaded || autoAppliedRef.current) return;
    autoAppliedRef.current = true;
    if (defaultView) {
      applyViewFilters(defaultView);
      setActiveViewId(defaultView.id);
    }
  }, [viewsLoaded, defaultView, applyViewFilters]);

  // Drop the selection if the active view disappears (deleted by us / a peer).
  useEffect(() => {
    if (activeViewId !== null && !views.some((v) => v.id === activeViewId)) {
      setActiveViewId(null);
    }
  }, [views, activeViewId]);

  if (!canViewContacts) {
    return null;
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="space-y-6 pb-24">
      <header className="flex items-end justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              Contacts
            </h1>
            {status === "ok" && (
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                {formatNumber(total)}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500">
            {project?.name ?? "—"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ColumnSelector
            available={CONTACT_COLUMNS}
            activeKeys={visibleColumnKeys}
            onChange={setVisibleColumnKeys}
            onReset={resetColumns}
            isCustomized={columnsDirty}
          />
          {canExport && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={exporting || status !== "ok" || total === 0 || !slug}
            >
              {exporting ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="mr-2 h-3.5 w-3.5" />
              )}
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={status === "loading" || !slug}
          >
            <RefreshCcw
              className={
                status === "loading"
                  ? "mr-2 h-3.5 w-3.5 animate-spin"
                  : "mr-2 h-3.5 w-3.5"
              }
            />
            Refresh
          </Button>
          {canCreate && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setCreateOpen(true)}
              disabled={!slug}
            >
              <Plus className="mr-2 h-3.5 w-3.5" />
              New contact
            </Button>
          )}
        </div>
      </header>

      {/* Row 2 (F6): Views dropdown on the left + ViewActionsCluster
       *  on the right when a named view is dirty. ContactsPage doesn't
       *  have a chips bar to relocate the cluster to (no advanced
       *  filters yet), so we keep both on the views row separated by
       *  ml-auto. The ColumnSelector stays in the page header on row
       *  1 — it's been there since the page shipped and there's no
       *  reason to move it. */}
      <div className="flex flex-wrap items-center gap-2">
        <ViewsDropdown
          views={views}
          loading={viewsLoading}
          activeView={activeView}
          canCreate={canCreateViews}
          canShare={canShareViews}
          onPickView={handlePickView}
          onRename={async (view, name) => {
            await renameView(view.id, name);
          }}
          onToggleShared={async (view) => {
            await setViewShared(view.id, !view.is_shared);
          }}
          onToggleDefault={async (view) => {
            await toggleDefault(view.id, view.is_default);
          }}
          onDelete={async (view) => {
            await deleteView(view.id);
          }}
          onCreateNewClick={() => setSaveAsNewOpen(true)}
        />
        <div className="ml-auto">
          <ViewActionsCluster
            activeView={activeView}
            isDirty={isDirty}
            canCreate={canCreateViews}
            canShare={canShareViews}
            savingActive={false}
            onUpdateActive={handleUpdateActiveView}
            onResetActive={handleResetActiveView}
            onCreateView={async ({ name, is_shared }) => {
              const created = await createView({
                name,
                filters: currentFiltersJson,
                columns: visibleColumnKeys,
                is_shared,
              });
              setActiveViewId(created.id);
              return created;
            }}
          />
        </div>
      </div>

      {/* F6: page-owned SaveAsNewViewModal. Driven by saveAsNewOpen,
       *  triggered by the ViewsDropdown's "+ Create new view" footer
       *  button. The cluster's chevron renders its own internal modal
       *  instance — separate concern. */}
      <SaveAsNewViewModal
        open={saveAsNewOpen}
        onOpenChange={setSaveAsNewOpen}
        canShare={canShareViews}
        onSave={async ({ name, is_shared }) => {
          const created = await createView({
            name,
            filters: currentFiltersJson,
            columns: visibleColumnKeys,
            is_shared,
          });
          setActiveViewId(created.id);
          return created;
        }}
      />

      <div className="flex flex-wrap items-center gap-3 rounded-plane bg-white p-4 shadow-plane">
        <div className="relative min-w-[260px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search a name, a company, an email or a phone number…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 rounded-lg border-slate-200 bg-slate-50/60 pl-9"
          />
        </div>

        <ContactFilterControls slug={slug ?? ""} state={contactFilters} />
      </div>

      {status === "error" && (
        <ErrorBanner message={errorMessage} onRetry={load} />
      )}

      {status === "loading" && items.length === 0 && showSkeleton ? (
        <motion.div
          key="contact-skeleton"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <ContactTableSkeleton rows={8} selectable={canEdit || canDelete} />
        </motion.div>
      ) : status === "loading" && items.length === 0 ? null : items.length === 0 ? (
        debouncedSearch.trim() ? (
          <NoMatch onReset={() => setSearch("")} />
        ) : (
          <EmptyState anyFilterActive={anyFilterActive} />
        )
      ) : (
        <>
          <ContactTable
            items={items}
            companyById={companyById}
            onRowClick={handleRowClick}
            selectable={canEdit || canDelete}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            visibleColumnKeys={visibleColumnKeys}
            availableColumns={CONTACT_COLUMNS}
          />
          <Pagination
            page={page}
            totalPages={totalPages}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            total={total}
            filteredCount={null}
            onChange={setPage}
          />
        </>
      )}

      <ContactDetailSheet
        contact={activeContact}
        company={
          activeContact ? companyById.get(activeContact.company_id) ?? null : null
        }
        projectSlug={slug ?? ""}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onEdit={handleEdit}
        onDelete={canDelete ? handleAskDelete : undefined}
        readOnly={!canEdit}
      />

      <ContactEditDialog
        contact={activeContact}
        company={
          activeContact ? companyById.get(activeContact.company_id) ?? null : null
        }
        projectSlug={slug ?? ""}
        open={editOpen}
        onOpenChange={(o) => {
          setEditOpen(o);
          if (!o && activeContact) setSheetOpen(true);
        }}
        onSaved={handleSaved}
      />

      <CreateContactDialog
        projectSlug={slug ?? ""}
        open={createOpen}
        onOpenChange={setCreateOpen}
        companies={companies}
        onCreated={handleCreated}
      />

      <DeleteContactDialog
        projectSlug={slug ?? ""}
        contact={activeContact}
        company={
          activeContact ? companyById.get(activeContact.company_id) ?? null : null
        }
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onDeleted={handleDeleted}
      />

      {(canEdit || canDelete) && slug && (
        <ContactsBulkActionsBar
          projectSlug={slug}
          selectedIds={Array.from(selectedIds)}
          canEdit={canEdit}
          canDelete={canDelete}
          onClear={clearSelection}
          onBulkUpdated={handleBulkUpdated}
          onBulkDeleted={handleBulkDeleted}
        />
      )}
    </div>
  );
}

interface PaginationProps {
  page: number;
  totalPages: number;
  rangeStart: number;
  rangeEnd: number;
  total: number;
  filteredCount: number | null;
  onChange: (page: number) => void;
}

function Pagination({
  page,
  totalPages,
  rangeStart,
  rangeEnd,
  total,
  filteredCount,
  onChange,
}: PaginationProps) {
  return (
    <div className="flex items-center justify-between text-xs text-slate-500">
      <div>
        {filteredCount != null ? (
          <span>
            <span className="font-medium text-slate-700">
              {filteredCount}
            </span>{" "}
            match{filteredCount === 1 ? "" : "es"} on this page
          </span>
        ) : (
          <span>
            <span className="font-medium text-slate-700">
              {rangeStart}–{rangeEnd}
            </span>{" "}
            of {formatNumber(total)}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </Button>
        <span className="px-2 text-slate-600">
          {page} / {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages}
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function EmptyState({ anyFilterActive }: { anyFilterActive: boolean }) {
  if (anyFilterActive) {
    return (
      <div className="rounded-plane bg-white px-6 py-16 text-center shadow-plane">
        <SearchX className="mx-auto mb-4 h-10 w-10 text-slate-300" />
        <p className="text-base font-medium text-slate-700">
          No contacts match these filters
        </p>
        <p className="mt-1 text-sm text-slate-500">
          Try widening your filters or clearing them.
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-plane bg-white px-6 py-16 text-center shadow-plane">
      <p className="text-base font-medium text-slate-700">No contacts yet</p>
      <p className="mt-1 text-sm text-slate-500">
        Contacts will appear here once the enrichment engine has run.
      </p>
    </div>
  );
}

function NoMatch({ onReset }: { onReset: () => void }) {
  return (
    <div className="rounded-plane bg-white px-6 py-16 text-center shadow-plane">
      <SearchX className="mx-auto mb-4 h-10 w-10 text-slate-300" />
      <p className="text-base font-medium text-slate-700">
        No matches on this page
      </p>
      <p className="mt-1 text-sm text-slate-500">
        Try a different search or clear it to see all contacts.
      </p>
      <Button variant="outline" size="sm" onClick={onReset} className="mt-4">
        Clear search
      </Button>
    </div>
  );
}

function ErrorBanner({
  message,
  onRetry,
}: {
  message: string | null;
  onRetry: () => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
      <div>
        <p className="text-sm font-medium text-rose-700">
          Couldn&rsquo;t load contacts
        </p>
        {message && <p className="text-xs text-rose-600">{message}</p>}
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onRetry}
        className="border-rose-200 bg-white text-rose-700 hover:bg-rose-100"
      >
        <RefreshCcw className="mr-2 h-3.5 w-3.5" />
        Retry
      </Button>
    </div>
  );
}


function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "An unexpected error occurred.";
}
